import { useState, useEffect, useCallback } from 'react';
export function useColumnPreferences(defaultColumns) {
    const [columns, setColumns] = useState(defaultColumns);
    const [loading, setLoading] = useState(true);
    // Load preferences from API
    useEffect(() => {
        loadPreferences();
    }, []);
    const loadPreferences = async () => {
        try {
            const response = await fetch('/api/user-preferences', {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                const prefs = data.preferences?.portfolioColumns;
                if (prefs?.columns) {
                    // Merge saved preferences with default columns
                    const updatedColumns = defaultColumns.map((col) => {
                        const saved = prefs.columns[col.id];
                        return saved
                            ? { ...col, ...saved }
                            : col;
                    }).sort((a, b) => a.order - b.order);
                    setColumns(updatedColumns);
                }
            }
        }
        catch (error) {
            console.error('Failed to load column preferences:', error);
        }
        finally {
            setLoading(false);
        }
    };
    const savePreferences = useCallback(async (cols) => {
        try {
            const prefs = {
                columns: cols.reduce((acc, col) => {
                    acc[col.id] = {
                        width: col.width,
                        order: col.order,
                        visible: col.visible,
                    };
                    return acc;
                }, {}),
            };
            await fetch('/api/user-preferences', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    preferences: { portfolioColumns: prefs },
                }),
            });
        }
        catch (error) {
            console.error('Failed to save column preferences:', error);
        }
    }, []);
    const updateColumnWidth = useCallback((columnId, width) => {
        setColumns((prev) => {
            const updated = prev.map((col) => col.id === columnId ? { ...col, width } : col);
            savePreferences(updated);
            return updated;
        });
    }, [savePreferences]);
    const reorderColumns = useCallback((fromIndex, toIndex) => {
        setColumns((prev) => {
            const updated = [...prev];
            const [moved] = updated.splice(fromIndex, 1);
            if (!moved)
                return prev; // Safety check
            updated.splice(toIndex, 0, moved);
            // Update order values
            const reordered = updated.map((col, index) => ({
                ...col,
                order: index,
            }));
            savePreferences(reordered);
            return reordered;
        });
    }, [savePreferences]);
    const resetToDefaults = useCallback(() => {
        setColumns(defaultColumns);
        savePreferences(defaultColumns);
    }, [defaultColumns, savePreferences]);
    return {
        columns,
        loading,
        updateColumnWidth,
        reorderColumns,
        resetToDefaults,
    };
}
//# sourceMappingURL=useColumnPreferences.js.map